import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { TableCell } from "@amzn/meridian/table";
import TimePicker from "@amzn/meridian/time-picker";
import { formatTime } from "../SettingsUtil";
import "../styles/OpsClockView.css";
import { parse, compareAsc, isValid } from "date-fns";
import { SettingsContext } from "./SettingsContext";

function limitParser(limit, selectedDate) {
  if (!limit) return null;
  if (isNaN(limit)) {
    return parse(limit, "HH:mm:ss", new Date(selectedDate));
  } else {
    return parse(limit, "t", new Date(selectedDate));
  }
}

const OpsClockProcessData = ({
  clockEvent,
  currentTime,
  oldTime,
  timeZone,
  dispatchDate,
  selectedDate,
  lowerLimit,
  upperLimit,
  handleClockEventTimeOverride,
  timeType,
  isUserAuthorisedForSettingsEdit,
}) => {
  const { dispatch } = useContext(SettingsContext);
  const oldDate = useMemo(
    () => (oldTime ? new Date(oldTime * 1000) : null),
    [oldTime],
  );
  const currentDate = useMemo(
    () => (currentTime ? new Date(currentTime * 1000) : null),
    [currentTime],
  );

  const formattedLowerLimit = useMemo(() => {
    return limitParser(lowerLimit, selectedDate);
  }, [lowerLimit, selectedDate]);

  const formattedUpperLimit = useMemo(() => {
    return limitParser(upperLimit, selectedDate);
  }, [upperLimit]);

  const timeValidator = useCallback(
    (time) => {
      return (
        (formattedLowerLimit
          ? compareAsc(formattedLowerLimit, time) <= 0
          : true) &&
        (formattedUpperLimit
          ? compareAsc(time, formattedUpperLimit) <= 0
          : true)
      );
    },
    [formattedLowerLimit, formattedUpperLimit],
  );

  useEffect(() => {
    const eventKey = `${clockEvent.process}-${clockEvent.cycleName}-${timeType}`;
    if (timeValidator(currentDate)) {
      dispatch({ type: "CLEAR_ERROR", payload: eventKey });
    } else {
      dispatch({ type: "ADD_ERROR", payload: eventKey });
    }
  }, [timeValidator, currentDate, dispatch, clockEvent, timeType]);

  const oldDateTimeString = useMemo(() => {
    return oldDate ? oldDate.toLocaleString("en-US", { timeZone }) : null;
  }, [oldDate]);

  const currentDateTimeString = useMemo(() => {
    return currentDate
      ? currentDate.toLocaleString("en-US", { timeZone })
      : dispatchDate;
  }, [currentTime, currentDate]);

  const formattedCurrentDate = useMemo(() => {
    if (
      (currentDate && oldDate && currentDate.getTime() !== oldDate.getTime()) ||
      (currentDate !== null && oldDate == null)
    ) {
      return formatTime(currentDateTimeString, "timePicker");
    } else {
      return null;
    }
  }, [currentDate, oldDate, currentDateTimeString]);

  const formattedProcessName = useMemo(() => {
    let processName = clockEvent.process.toLowerCase();
    if (processName === "pick") processName = `${processName} Stage `;
    if (processName === "twilight") processName = `sort_0 `;
    return `${processName} ${timeType.substring(0, timeType.length - 4)}`;
  }, [clockEvent, timeType]);

  const isValidTime = React.useMemo(() => {
    if (!isValid(currentDate)) {
      return false;
    }
    return timeValidator(currentDate);
  }, [currentDate, timeValidator]);

  const internalHandler = useCallback(
    (e) => {
      handleClockEventTimeOverride(
        e,
        currentTime,
        clockEvent.process,
        clockEvent.cycleName,
        currentDateTimeString,
        timeType,
      );
    },
    [
      currentTime,
      timeValidator,
      handleClockEventTimeOverride,
      dispatch,
      currentDateTimeString,
      clockEvent,
    ],
  );

  return (
    <React.Fragment>
      <TableCell>
        <div className="process">
          {/* GetOpsClock API returns processes as INDUCT, STOW, DISPATCH, PICK
                          If PICK process is encountered it will add Stage to the process name as well.
                      */}
          <span className="processName">{formattedProcessName}</span>
        </div>
      </TableCell>
      <TableCell>
        {oldDateTimeString ? formatTime(oldDateTimeString) : "N/A"}
      </TableCell>
      <TableCell>
        <TimePicker
          value={formattedCurrentDate}
          onChange={internalHandler}
          width={150}
          format={{ hourCycle: "h23", hour: "2-digit", minute: "2-digit" }}
          size="small"
          error={!isValidTime}
          showIcon={false}
          placeholder="HH:MM"
          invalidInputMessage="Invalid time. The correct time format is hour:minute."
          disabled={!isUserAuthorisedForSettingsEdit}
        />
      </TableCell>
    </React.Fragment>
  );
};

export default OpsClockProcessData;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/OpsClockProcessData.js